import { X, Printer } from "lucide-react";
import { printThermal, esc, rs } from "@/lib/thermal";
import { formatMoney } from "@/lib/api";

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—");
const fmtDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
const isRange = (r) => r.date_from !== r.date_to;
const rangeText = (r) => (isRange(r) ? `${fmtDate(r.date_from)} – ${fmtDate(r.date_to)}` : fmtDate(r.date_from));
const shortDay = (d) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: "2-digit", month: "short" });

const row = (label, value, cls = "") => `<tr class="${cls}"><td class="name">${label}</td><td class="amt">${value}</td></tr>`;

export function buildClosingHTML(r, printedBy) {
  const expenses = r.expenses.length
    ? r.expenses.map((e) => row(esc(e.description), rs(e.amount))).join("")
    : `<tr><td class="muted">No expenses recorded</td></tr>`;
  const items = r.top_items.length
    ? r.top_items.map((it) => `<tr><td class="qty">${it.qty}×</td><td class="name">${esc(it.name)}</td><td class="amt">${rs(it.revenue)}</td></tr>`).join("")
    : `<tr><td class="muted">No sales</td></tr>`;
  const daily = isRange(r)
    ? `<hr/><h2>Day-by-day sales</h2><table>${r.daily.map((d) => `<tr><td class="name">${shortDay(d.date)}</td><td class="qty">${d.orders}</td><td class="amt">${rs(d.revenue)}</td></tr>`).join("")}</table>`
    : "";
  const when = isRange(r)
    ? `Period: <b>${rangeText(r)}</b>`
    : `Date: <b>${rangeText(r)}</b><br/>Bills: ${fmtTime(r.first_order_at)} – ${fmtTime(r.last_order_at)}`;
  return `<h1>TEABOY</h1>
<div class="tag">${esc(r.outlet_name)}</div>
<div class="title">${isRange(r) ? "PERIOD SUMMARY" : "DAY END REPORT"}</div>
<div class="meta">${when}<br/>Printed: ${new Date().toLocaleString()}</div>
<hr/><h2>Sales summary</h2>
<table>${row("Total orders", r.order_count)}${row("Items sold", r.items_sold)}${row("Average bill", rs(r.avg_ticket))}${row("GROSS SALES", rs(r.revenue), "total")}</table>
<hr/><h2>Payment split</h2>
<table>${row("Cash", rs(r.cash_total))}${row("Card", rs(r.card_total))}${row("UPI", rs(r.upi_total))}${row("Total", rs(r.revenue), "sub")}</table>
<hr/><h2>Expenses</h2>
<table>${expenses}${row("Total expenses", rs(r.expense_total), "sub")}</table>
<hr/>
<table>${row("CASH IN HAND", rs(r.net_cash), "total")}</table>
<div class="muted">Cash sales − expenses</div>
<table>${row("Net (sales − expenses)", rs(r.net_total), "sub")}</table>
${daily}
<hr/><h2>Top items</h2>
<table>${items}</table>
<hr/>
<div class="foot">${printedBy ? "Closed by " + esc(printedBy) : ""}</div>
<div class="tagline">chai for everyone</div>`;
}

function Line({ label, value, strong }) {
  return (
    <div className={`flex justify-between gap-3 ${strong ? "font-extrabold text-base" : ""}`}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

export default function ClosingReportModal({ report: r, printedBy, onClose }) {
  const title = isRange(r) ? "Period Summary" : "Day End Report";
  const print = () => printThermal(`${title} ${rangeText(r)}`, buildClosingHTML(r, printedBy));
  const m = (n) => `₹${formatMoney(n)}`;
  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 flex items-center justify-center p-4" onClick={onClose} data-testid="closing-report-modal">
      <div className="readable bg-card border border-border w-full max-w-sm max-h-[90vh] flex flex-col animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-lg font-bold" data-testid="closing-report-title">{title}</div>
            <div className="text-xs text-muted-foreground">{r.outlet_name} · {rangeText(r)}</div>
          </div>
          <button data-testid="closing-report-close" onClick={onClose} className="p-1 hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 text-sm space-y-3 overflow-y-auto thin-scroll" style={{ fontFamily: "Arial, Helvetica, sans-serif" }} data-testid="closing-report-preview">
          <Section title="Sales summary">
            <Line label="Total orders" value={r.order_count} />
            <Line label="Items sold" value={r.items_sold} />
            <Line label="Average bill" value={m(r.avg_ticket)} />
            <Line label="Gross sales" value={m(r.revenue)} strong />
          </Section>
          <Section title="Payment split">
            <Line label="Cash" value={m(r.cash_total)} />
            <Line label="Card" value={m(r.card_total)} />
            <Line label="UPI" value={m(r.upi_total)} />
          </Section>
          <Section title="Expenses">
            {r.expenses.length === 0 && <div className="text-muted-foreground text-xs">No expenses recorded</div>}
            {r.expenses.map((e) => <Line key={e.id} label={e.description} value={m(e.amount)} />)}
            <Line label="Total expenses" value={m(r.expense_total)} strong />
          </Section>
          {isRange(r) && (
            <Section title="Day-by-day sales">
              {r.daily.map((d) => <Line key={d.date} label={`${shortDay(d.date)} · ${d.orders} orders`} value={m(d.revenue)} />)}
            </Section>
          )}
          <div className="border-t-2 border-foreground pt-3" data-testid="closing-cash-in-hand">
            <Line label="CASH IN HAND" value={m(r.net_cash)} strong />
            <div className="text-xs text-muted-foreground">Cash sales − expenses · Net {m(r.net_total)}</div>
          </div>
        </div>
        <div className="border-t border-border p-4 grid grid-cols-2 gap-2">
          <button data-testid="closing-report-print-btn" onClick={print} className="flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 text-sm font-bold hover:opacity-90 active:scale-[0.98] transition-transform">
            <Printer strokeWidth={1.75} className="w-4 h-4" /> Print
          </button>
          <button data-testid="closing-report-done-btn" onClick={onClose} className="border border-border py-3 text-sm font-semibold hover:bg-muted">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-t border-dashed border-border pt-3 space-y-1.5 first:border-t-0 first:pt-0">
      <div className="text-xs font-extrabold uppercase tracking-wider">{title}</div>
      {children}
    </div>
  );
}
